import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Sparkles, CheckCircle2, Clock, Ban, LinkIcon } from 'lucide-react';
import { atlasApi, DiscPublico as DiscPublicoDados, DiscResposta } from '../shared/atlasApi';
import PassosQuestionario from './PassosQuestionario';

type Etapa = 'carregando' | 'bloqueado' | 'inicio' | 'respondendo' | 'enviado';
type Motivo = 'invalido' | 'expirado' | 'respondido' | 'cancelado' | 'erro';

const BLOQUEIOS: Record<Motivo, { icone: React.ReactNode; titulo: string; texto: string }> = {
  respondido: {
    icone: <CheckCircle2 className="w-10 h-10 text-emerald-500" />,
    titulo: 'Questionário já respondido',
    texto: 'Suas respostas já chegaram para o RH. Não precisa fazer mais nada.',
  },
  expirado: {
    icone: <Clock className="w-10 h-10 text-amber-500" />,
    titulo: 'Este link expirou',
    texto: 'O link vale por 8 horas. Peça um novo para o RH.',
  },
  cancelado: {
    icone: <Ban className="w-10 h-10 text-gray-400" />,
    titulo: 'Este link não vale mais',
    texto: 'O RH gerou um link novo ou cancelou este. Use o link mais recente que você recebeu.',
  },
  invalido: {
    icone: <LinkIcon className="w-10 h-10 text-gray-400" />,
    titulo: 'Link não encontrado',
    texto: 'Confira se o link foi copiado inteiro. Se continuar, peça um novo para o RH.',
  },
  erro: {
    icone: <LinkIcon className="w-10 h-10 text-gray-400" />,
    titulo: 'Não foi possível abrir o questionário',
    texto: 'Tente de novo em alguns minutos.',
  },
};

function motivoDoErro(e: any): Motivo {
  const m = e?.body?.motivo;
  if (m === 'invalido' || m === 'expirado' || m === 'respondido' || m === 'cancelado') return m;
  return 'erro';
}

const Moldura: React.FC<{ empresa?: string | null; children: React.ReactNode }> = ({ empresa, children }) => (
  <div className="min-h-screen bg-gray-50">
    <header className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
      <div className="max-w-2xl mx-auto px-4 py-5 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-purple-200" />
        <div>
          <div className="font-bold leading-tight">Perfil comportamental</div>
          {empresa && <div className="text-xs text-purple-200">{empresa}</div>}
        </div>
      </div>
    </header>
    <main className="max-w-2xl mx-auto px-4 py-6">{children}</main>
  </div>
);

/** Página aberta pelo link que o RH envia. Sem login; não mostra o resultado. */
const DiscPublico: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [etapa, setEtapa] = useState<Etapa>('carregando');
  const [motivo, setMotivo] = useState<Motivo>('erro');
  const [dados, setDados] = useState<DiscPublicoDados | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    atlasApi.discPublicoAbrir(token)
      .then(r => { if (!cancel) { setDados(r); setEtapa('inicio'); } })
      .catch(e => { if (!cancel) { setMotivo(motivoDoErro(e)); setEtapa('bloqueado'); } });
    return () => { cancel = true; };
  }, [token]);

  // Evita perder as respostas fechando a aba sem querer
  useEffect(() => {
    if (etapa !== 'respondendo') return;
    const aviso = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [etapa]);

  const enviar = async (respostas: DiscResposta[]) => {
    setSalvando(true);
    setErro(null);
    try {
      await atlasApi.discPublicoEnviar(token, respostas);
      setEtapa('enviado');
      window.scrollTo(0, 0);
    } catch (e: any) {
      const m = motivoDoErro(e);
      if (m === 'erro') {
        setErro(e?.message || 'Não foi possível enviar. Tente de novo.');
      } else {
        setMotivo(m);
        setEtapa('bloqueado');
      }
    } finally {
      setSalvando(false);
    }
  };

  if (etapa === 'carregando') {
    return (
      <Moldura>
        <div className="py-16 text-center text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...
        </div>
      </Moldura>
    );
  }

  if (etapa === 'bloqueado') {
    const b = BLOQUEIOS[motivo];
    return (
      <Moldura>
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center space-y-3">
          <div className="flex justify-center">{b.icone}</div>
          <h1 className="text-lg font-bold text-gray-900">{b.titulo}</h1>
          <p className="text-sm text-gray-500">{b.texto}</p>
        </div>
      </Moldura>
    );
  }

  if (etapa === 'enviado') {
    return (
      <Moldura empresa={dados?.empresa}>
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center space-y-3">
          <div className="flex justify-center"><CheckCircle2 className="w-12 h-12 text-emerald-500" /></div>
          <h1 className="text-xl font-bold text-gray-900">Obrigado{dados?.nome ? `, ${dados.nome}` : ''}!</h1>
          <p className="text-sm text-gray-500">
            Suas respostas foram enviadas para o RH. Pode fechar esta página.
          </p>
        </div>
      </Moldura>
    );
  }

  if (etapa === 'inicio' && dados) {
    const prazo = new Date(dados.expira_em).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
    return (
      <Moldura empresa={dados.empresa}>
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <h1 className="text-xl font-bold text-gray-900">Olá{dados.nome ? `, ${dados.nome}` : ''}!</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            O RH pediu que você responda este questionário de perfil comportamental (DISC).
            São <strong>{dados.grupos.length} perguntas</strong> e leva uns 10 minutos.
          </p>
          <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
            <li>Em cada pergunta, marque a palavra que <strong className="text-emerald-600">mais</strong> combina
              com você e a que <strong className="text-rose-600">menos</strong> combina.</li>
            <li>Não existe resposta certa ou errada. Responda pensando em como você é, sem pensar muito.</li>
            <li>Responda tudo de uma vez: se fechar a página, as respostas se perdem.</li>
          </ul>
          <p className="text-xs text-gray-400">Este link é pessoal e vale até {prazo}.</p>
          <button
            onClick={() => setEtapa('respondendo')}
            className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold"
          >
            Começar
          </button>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura empresa={dados?.empresa}>
      <PassosQuestionario
        grupos={dados?.grupos || []}
        salvando={salvando}
        erro={erro}
        onConcluir={enviar}
      />
    </Moldura>
  );
};

export default DiscPublico;
